import GridDrawer from '@/components/common/GridDrawer';
import { useMemo, useState } from 'react';
import { Breadcrumb } from 'react-bootstrap';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import ToolTabs from '@/app/common/components/ToolTabs';
import { toolRoutes } from '@/routes/siteMaps';
import { useSelector } from 'react-redux';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import countSuffix from '@/helpers/countSuffix';
import { useCountCrawlChecklistQuery } from '@/app/Crawl/api/checklistsApi';
import SubtleBadge from '@/components/common/SubtleBadge';
import { NewCrawlContext, RemoveCrawlContext } from '@/context/Context';
import useDomain from '@/hooks/useDomain';

type CrawlRoute = {
  name: string;
  to: string;
  children?: CrawlRoute[];
};

type CrawlRouteGroup = {
  name: string;
  children: CrawlRoute[];
};

type CrawlAppState = {
  api?: {
    crawlHistoryApi?: unknown;
  };
};

type ChecklistCount = {
  category: string;
  count: number;
};

type BreadcrumbItem = {
  text: string;
  href: string;
};

type ChecklistResponse = {
  data?: {
    checklist_categories?: ChecklistCount[];
  };
};

const CrawlMain = () => {
  const { slug } = useParams();
  const { domain, domain_slug } = useDomain();
  const {
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useGetUserInfoQuery();
  // REMOVE CRAWL
  const [rem_job_page, setRem_job_page] = useState('');
  const [rem_start_url, setRem_start_url] = useState('');
  const [show, setShow] = useState(false);
  //
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>(
    []
  );
  //
  const routes = toolRoutes.children.find(
    item => item.name === 'خزش سایت'
  ) as CrawlRouteGroup;
  const countAnchorTexts = useSelector(
    (state: CrawlAppState) => state.api?.crawlHistoryApi
  );
  void countAnchorTexts;
  const location2 = useLocation();

  const isRouteActive = (
    route: CrawlRoute,
    pathname: string,
    navLinkActive = false
  ) => {
    if (route.to.includes('linkgraph') && pathname.includes('linkgraph')) {
      return true;
    }
    if (navLinkActive) {
      return true;
    }
    const linkPath = route.to.includes('history')
      ? `${route.to}/${domain_slug}/${domain}`
      : `${route.to}/${slug ? slug : 'last'}/${domain_slug}/${domain}`;
    return pathname === linkPath || pathname.startsWith(`${linkPath}/`);
  };

  const [navItems] = useState<CrawlRouteGroup>(routes);

  const activePage = useMemo(
    () =>
      navItems?.children?.find(route =>
        isRouteActive(route, location2.pathname)
      ),
    [navItems, location2.pathname, domain_slug, domain, slug]
  );

  // history route has no :slug; "last" and a missing slug both mean the latest crawl.
  const job_page = !slug || slug === 'last' ? domain_slug : slug;
  const { data: rawChecklistData } = useCountCrawlChecklistQuery(
    { job_page, domain_slug },
    { skip: !job_page || !domain_slug }
  );
  const data = rawChecklistData as ChecklistResponse | undefined;

  const countData = (data?.data?.checklist_categories ??
    []) as ChecklistCount[];

  // CONTEXT
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [jobPage, setJobPage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [canceled, setCanceled] = useState(false);

  if (!isLoading) {
    return (
      <NewCrawlContext.Provider
        value={{
          config,
          setConfig,
          jobPage,
          setJobPage,
          isSubmitted,
          setIsSubmitted,
          canceled,
          setCanceled
        }}
      >
        <RemoveCrawlContext.Provider
          value={{
            jobPage: rem_job_page,
            setJobPage: setRem_job_page,
            setStart_url: setRem_start_url,
            start_url: rem_start_url,
            show,
            setShow
          }}
        >
          <div className="my-3 fs--2">
            <Breadcrumb className="text-primary">
              <Breadcrumb.Item href="https://mizfa.tools">خانه</Breadcrumb.Item>
              <Breadcrumb.Item href="/">پیشخوان</Breadcrumb.Item>
              <Breadcrumb.Item href={`/crawl/history/${domain_slug}/${domain}`}>
                خزش
              </Breadcrumb.Item>
              {breadcrumbData?.map((b, i) => (
                <Breadcrumb.Item
                  active={breadcrumbData.length === i + 1}
                  key={i}
                  href={b?.href}
                >
                  {b?.text}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </div>
          <ToolTabs
            items={navItems.children.map(route => {
              const errorCount =
                route.to === '/crawl/check-list'
                  ? countSuffix(
                      countData?.find(
                        (item: ChecklistCount) => item.category === 'error'
                      )?.count
                    )
                  : '';
              return {
                key: route.to,
                label: route.name,
                to: route.to.includes('history')
                  ? `${route.to}/${domain_slug}/${domain}`
                  : `${route.to}/${slug ? slug : 'last'}/${domain_slug}/${domain}`,
                state: { path: location2.pathname, query: null },
                active: isRouteActive(route, location2.pathname),
                badge: errorCount ? (
                  <SubtleBadge bg="danger" className="ms-1">
                    {errorCount}
                  </SubtleBadge>
                ) : undefined
              };
            })}
          />
          {activePage && <RouteHelperAlerts route={activePage} />}
          <GridDrawer />
          {shouldShowDataLoadError(isError, error) ? (
            <DataLoadError
              onRetry={() => refetch()}
              isRetrying={isFetching}
              isNetworkError={isNetworkApiError(error)}
            />
          ) : (
            <Outlet context={[breadcrumbData, setBreadcrumbData]} />
          )}
        </RemoveCrawlContext.Provider>
      </NewCrawlContext.Provider>
    );
  }
};

export default CrawlMain;
